import { useEffect } from "react";
import { useSettings, AppearanceSettings } from "./useSettings";

function hexToHSL(hex: string): { h: number; s: number; l: number } {
  // Remove # if present
  hex = hex.replace(/^#/, "");

  // Parse hex values
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function applyPrimaryColor(hexColor: string) {
  const hsl = hexToHSL(hexColor);
  const hslValue = `${hsl.h} ${hsl.s}% ${hsl.l}%`;
  
  // Light mode values
  const root = document.documentElement;
  
  // Set primary color
  root.style.setProperty("--primary", hslValue);
  root.style.setProperty("--ring", hslValue);
  
  // Set sidebar primary to match
  root.style.setProperty("--sidebar-primary", hslValue);
  root.style.setProperty("--sidebar-ring", hslValue);
  
  // Gold-related variables (for theming consistency)
  root.style.setProperty("--gold", hslValue);
  root.style.setProperty("--gold-light", `${hsl.h} ${hsl.s}% 92%`);
  root.style.setProperty("--gold-dark", `${hsl.h} ${hsl.s}% ${Math.max(hsl.l - 10, 20)}%`);
  
  // Accent colors
  root.style.setProperty("--accent", `${hsl.h} ${hsl.s}% 95%`);
  root.style.setProperty("--accent-foreground", `${hsl.h} ${hsl.s}% 35%`);
  
  // Update gradient
  const gradientValue = `linear-gradient(135deg, hsl(${hsl.h}, ${hsl.s}%, ${Math.min(hsl.l + 5, 100)}%) 0%, hsl(${hsl.h}, ${hsl.s}%, ${Math.max(hsl.l - 5, 0)}%) 100%)`;
  root.style.setProperty("--gradient-gold", gradientValue);
  
  // Shadow with primary color
  root.style.setProperty("--shadow-gold", `0 4px 14px 0 hsl(${hsl.h} ${hsl.s}% ${hsl.l}% / 0.25)`);
}

export function useApplySettings() {
  const { getSetting, isLoading } = useSettings();

  useEffect(() => {
    if (isLoading) return;

    const appearance = getSetting<AppearanceSettings>("appearance");
    
    if (appearance?.primary_color) {
      applyPrimaryColor(appearance.primary_color);
    }
  }, [getSetting, isLoading]);

  return { applyPrimaryColor };
}

export { hexToHSL, applyPrimaryColor };
